import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Contract, ContractLine, ContractLineCreate, PriceList, PricingHeader, Customer, ContractCreate, Controller } from '../types';
import Modal from '../components/Modal';
import ContractLineForm from '../components/ContractLineForm';
import ContractAssignment from '../components/ContractAssignment';
import ContractForm from '../components/ContractForm';
import ContractDocuments from '../components/ContractDocuments';
import ChangePlanModal from '../components/ChangePlanModal';
import BulkEditContractLinesModal from '../components/BulkEditContractLinesModal';
import DebouncedCustomAmountInput from '../components/DebouncedCustomAmountInput';
import ContractRecipients from '../components/ContractRecipients';
import BillingDateManagerModal from '../components/BillingDateManagerModal';
import VatCheckButton from '../components/VatCheckButton';
import DebugBillingPeriodsModal from '../components/modals/DebugBillingPeriodsModal';

const ContractDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [contract, setContract] = useState<Contract | null>(null);
  const [contractLines, setContractLines] = useState<ContractLine[]>([]);
  const [priceLists, setPriceLists] = useState<PriceList[]>([]);
  const [pricingHeaders, setPricingHeaders] = useState<PricingHeader[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showLineModal, setShowLineModal] = useState(false);
  const [editingLine, setEditingLine] = useState<ContractLine | null>(null);
  const [showContractModal, setShowContractModal] = useState(false);
  const [addingControllers, setAddingControllers] = useState(false);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [activeTab, setActiveTab] = useState<'lines' | 'documents' | 'invoices' | 'recipients'>('lines');
  const [contractInvoices, setContractInvoices] = useState<any[]>([]);
  const [showFinalizedOnly, setShowFinalizedOnly] = useState(true);
  const [selectedReportLanguage, setSelectedReportLanguage] = useState<'en' | 'de' | 'pt'>('en');
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [selectedLinesForPlanChange, setSelectedLinesForPlanChange] = useState<ContractLine[]>([]);
  const [selectedContractLines, setSelectedContractLines] = useState<string[]>([]);
  const [editingContract, setEditingContract] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showBillingDateManager, setShowBillingDateManager] = useState(false);
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [loadingDebug, setLoadingDebug] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [bulkInvoiceFeedback, setBulkInvoiceFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);


  useEffect(() => {
    if (id) {
      fetchContractDetails();
      fetchContractLines();
      fetchPriceLists();
      fetchPricingHeaders();
      fetchCustomers();
      fetchContractInvoices();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchContractDetails = async () => {
    try {
      const response = await apiService.getContract(id!);
      setContract(response);
      
      // Also fetch customer details
      if (response.customer_id) {
        try {
          const customerData = await apiService.getCustomer(response.customer_id);
          setCustomer(customerData);
        } catch (error) {
          console.error('Error fetching customer:', error);
        }
      }
    } catch (error) {
      console.error('Error fetching contract details:', error);
    }
  };

  const fetchContractLines = async () => {
    try {
      setLoading(true);
      const response = await apiService.getContractLines(id!);
      setContractLines(response);
    } catch (error) {
      console.error('Error fetching contract lines:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPriceLists = async () => {
    try {
      const response = await apiService.getPriceLists(1, 100);
      setPriceLists(response.items);
    } catch (error) {
      console.error('Error fetching price lists:', error);
    }
  };

  const fetchPricingHeaders = async () => {
    try {
      const response = await apiService.getPricingHeaders(0, 100);
      setPricingHeaders(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching pricing headers:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await apiService.getCustomers(1, 100);
      setCustomers(Array.isArray(response.items) ? response.items : []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchContractInvoices = async () => {
    try {
      const response = await apiService.getInvoicesByContract(id!);
      setContractInvoices(response);
    } catch (error) {
      console.error('Error fetching contract invoices:', error);
    }
  };

  const handleCreateLine = () => {
    setEditingLine(null);
    setShowLineModal(true);
  };

  const handleEditLine = (line: ContractLine) => {
    setEditingLine(line);
    setShowLineModal(true);
  };

  const handleDeleteLine = async (lineId: string) => {
    if (window.confirm('Are you sure you want to delete this contract line?')) {
      try {
        await apiService.deleteContractLine(lineId);
        fetchContractLines();
      } catch (error) {
        console.error('Error deleting contract line:', error);
      }
    }
  };

  const handleChangePlanSingle = (line: ContractLine) => {
    setSelectedLinesForPlanChange([line]);
    setShowChangePlanModal(true);
  };

  const handleChangePlanBulk = () => {
    const selectedLines = contractLines.filter(line => 
      selectedContractLines.includes(line.contractline_id)
    );
    if (selectedLines.length === 0) {
      alert('Please select contract lines to change plans');
      return;
    }
    setSelectedLinesForPlanChange(selectedLines);
    setShowChangePlanModal(true);
  };

  const handleBulkEdit = () => {
    const selectedLines = contractLines.filter(line => 
      selectedContractLines.includes(line.contractline_id)
    );
    if (selectedLines.length === 0) {
      alert('Please select contract lines to edit');
      return;
    }
    setShowBulkEditModal(true);
  };

  const handleBulkEditSubmit = async (updates: Array<{
    contractLineId: string;
    updates: {
      pricelist_id?: number;
      custom_amount?: number | null;
      tax_rate?: number;
      discount_type?: string;
      discount_value?: number;
      start?: string;
      end?: string;
      trial_period?: number;
    };
  }>) => {
    try {
      // Separate date updates from other updates
      const dateUpdates = updates.filter(update => 
        update.updates.start || update.updates.end || update.updates.trial_period !== undefined
      );
      const otherUpdates = updates.filter(update => 
        update.updates.pricelist_id || update.updates.custom_amount !== undefined || 
        update.updates.tax_rate !== undefined || update.updates.discount_type || 
        update.updates.discount_value !== undefined
      );

      let successCount = 0;
      let errors: string[] = [];

      // Handle date updates using bulk API
      if (dateUpdates.length > 0) {
        try {
          // Check if all date updates have the same values (bulk update requirement)
          const firstUpdate = dateUpdates[0].updates;
          const canBulkUpdate = dateUpdates.every(update => 
            update.updates.start === firstUpdate.start &&
            update.updates.end === firstUpdate.end &&
            update.updates.trial_period === firstUpdate.trial_period
          );

          if (canBulkUpdate) {
            // Use bulk API
            const contractLineIds = dateUpdates.map(update => update.contractLineId);
            const result = await apiService.contracts.bulkUpdateContractLineDates(
              contractLineIds,
              firstUpdate.start,
              firstUpdate.end,
              firstUpdate.trial_period,
              'admin' // TODO: Get actual user
            );
            
            successCount += result.success_count;
            if (result.errors.length > 0) {
              errors.push(...result.errors.map(err => `${err.contractline_id}: ${err.error}`));
            }
          } else {
            // Fall back to individual updates for date fields
            for (const update of dateUpdates) {
              try {
                await apiService.updateContractLine(update.contractLineId, update.updates);
                successCount++;
              } catch (error: any) {
                const line = contractLines.find(l => l.contractline_id === update.contractLineId);
                errors.push(`${line?.contractline_name || update.contractLineId}: ${error.message || 'Failed to update'}`);
              }
            }
          }
        } catch (error: any) {
          errors.push(`Bulk date update failed: ${error.message || 'Unknown error'}`);
        }
      }

      // Handle other updates individually
      for (const update of otherUpdates) {
        try {
          await apiService.updateContractLine(update.contractLineId, update.updates);
          successCount++;
        } catch (error: any) {
          const line = contractLines.find(l => l.contractline_id === update.contractLineId);
          errors.push(`${line?.contractline_name || update.contractLineId}: ${error.message || 'Failed to update'}`);
        }
      }

      // Show results
      let message = `Successfully updated ${successCount} contract line(s).`;
      if (errors.length > 0) {
        message += `\n\nErrors:\n${errors.join('\n')}`;
      }
      alert(message);

      setShowBulkEditModal(false);
      setSelectedContractLines([]);
      fetchContractLines();
    } catch (error) {
      console.error('Error in bulk edit:', error);
      alert('Failed to update contract lines. Please try again.');
    }
  };

  const handlePlanChangeSubmit = async (updates: { contractLineId: string; priceListId: number; discount?: { type: string; value: number } }[]) => {
    try {
      for (const update of updates) {
        const line = contractLines.find(l => l.contractline_id === update.contractLineId);
        if (line) {
          const updateData: any = {
            pricelist_id: update.priceListId
          };
          
          if (update.discount) {
            updateData.discount_type = update.discount.type;
            updateData.discount_value = update.discount.type === 'percentage' 
              ? Math.round(update.discount.value * 100) // Store percentage as integer (e.g., 10% = 1000)
              : Math.round(update.discount.value * 100); // Store fixed amount in cents
          }
          
          await apiService.updateContractLine(update.contractLineId, updateData);
        }
      }
      
      setShowChangePlanModal(false);
      setSelectedContractLines([]);
      fetchContractLines();
      alert('Plans updated successfully');
    } catch (error) {
      console.error('Error updating plans:', error);
      alert('Failed to update plans. Please try again.');
    }
  };

  const handleSubmitLine = async (data: ContractLineCreate) => {
    try {
      console.log('Contract ID from URL:', id);
      console.log('Contract Line Data being submitted:', data);
      
      if (editingLine) {
        await apiService.updateContractLine(editingLine.contractline_id, data);
      } else {
        await apiService.createContractLine(data);
      }
      setShowLineModal(false);
      fetchContractLines();
    } catch (error: any) {
      console.error('Error saving contract line:', error);
      if (error.response?.data?.detail) {
        console.error('Validation error details:', error.response.data.detail);
        
        // Better error message handling
        let errorMessage = 'Validation error: ';
        if (Array.isArray(error.response.data.detail)) {
          errorMessage += error.response.data.detail.map((err: any) => 
            `${err.loc ? err.loc.join(' -> ') : 'Field'}: ${err.msg}`
          ).join('\n');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage += error.response.data.detail;
        } else {
          errorMessage += JSON.stringify(error.response.data.detail);
        }
        
        alert(errorMessage);
      } else {
        alert('An error occurred while saving the contract line. Please check the console for details.');
      }
    }
  };

  const handleSubmitContract = async (data: ContractCreate) => {
    try {
      if (editingContract && contract) {
        // Update existing contract - merge unchanged fields to satisfy backend NOT NULL constraints
        const updatedPayload = {
          ...contract,
          ...data
        };
        await apiService.updateContract(contract.contract_id, updatedPayload);
        setEditingContract(false);
      } else {
        // Create new contract
        const result = await apiService.createContract(data);
        
        // Check if this was a supervisor contract creation
        if ('contract' in result && 'contract_lines' in result) {
          console.log('Supervisor contract created:', {
            contractId: result.contract.contract_id,
            linesCreated: result.contract_lines.length,
            errors: result.errors
          });
        }
      }
      
      setShowContractModal(false);
      fetchContractDetails();
    } catch (error) {
      console.error('Error saving contract:', error);
      alert('An error occurred while saving the contract. Please check the console for details.');
    }
  };

  const handleCreateInvoice = () => {
    if (contract) {
      navigate('/invoices', { state: { contractId: contract.contract_id, contractName: contract.contract_name } });
    }
  };

  const handleAddAllControllers = async () => {
    if (!contract || !customer) return;

    // Check if this is a supervisor contract
    if (customer.customer_type !== 'supervisor' || !customer.supervisor_id) {
      alert('This contract is not associated with a supervisor customer.');
      return;
    }

    try {
      setAddingControllers(true);

      // Get all controllers for this supervisor
      const controllers = await apiService.getControllersBySupervisor(customer.supervisor_id);
      
      if (controllers.length === 0) {
        alert('No controllers found for this supervisor.');
        return;
      }

      // Check which controllers already have contract lines
      const existingControllerNames = contractLines.map(line => line.contractline_name.toLowerCase());
      const controllersToAdd = controllers.filter(
        (controller: Controller) => !existingControllerNames.includes(controller.controller_name.toLowerCase())
      );

      if (controllersToAdd.length === 0) {
        alert('All controllers are already added to this contract.');
        return;
      }

      // Confirm action
      const confirmMessage = `Add ${controllersToAdd.length} controller(s) to this contract?\n\nControllers to add:\n${controllersToAdd.map((c: Controller) => c.controller_name).join('\n')}`;
      if (!window.confirm(confirmMessage)) {
        return;
      }

      // Add each controller
      let successCount = 0;
      let errors: string[] = [];

      for (const controller of controllersToAdd) {
        try {
          // Create a contract line for each controller
          const contractLineData: ContractLineCreate = {
            contractline_name: controller.controller_name,
            contract_id: contract.contract_id,
            pricelist_id: priceLists[0]?.pricelist_id || 1, // Use first available price list or default
            billing_cycle: '1_month',
            start: new Date().toISOString().split('T')[0],
            is_active: true,
            trial_period: 0,
            contractline_createdby: contract.contract_createdby,
            isactive: true,
            isdeleted: false,
            iscurrent: true
          };

          await apiService.createContractLine(contractLineData);
          successCount++;
        } catch (error: any) {
          errors.push(`${controller.controller_name}: ${error.message || 'Failed to add'}`);
        }
      }

      // Show results
      let message = `Successfully added ${successCount} controller(s) to the contract.`;
      if (errors.length > 0) {
        message += `\n\nErrors:\n${errors.join('\n')}`;
      }
      alert(message);

      // Refresh contract lines
      fetchContractLines();
      
    } catch (error) {
      console.error('Error adding controllers:', error);
      alert('An error occurred while adding controllers. Please check the console for details.');
    } finally {
      setAddingControllers(false);
    }
  };

  
  const getPriceListDetails = (id: number) => {
    const priceList = priceLists.find(p => p.pricelist_id === id);
    if (!priceList) return null;
    return {
      name: priceList.name,
      amount: priceList.amount,
      currency: priceList.currency,
      billingPeriod: priceList.billing_period,
      planName: priceList.plan?.name || 'N/A',
      pricingHeaderName: priceList.pricing_header?.name || 'N/A'
    };
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatBillingCycle = (cycle: string) => {
    const cycleMap: { [key: string]: string } = {
      '7 days': 'Weekly',
      '30 days': 'Monthly',
      '90 days': 'Quarterly',
      '365 days': 'Yearly',
      '1_month': 'Monthly',
      '3_months': 'Quarterly',
      '6_months': 'Semi-Annually',
      '12_months': 'Yearly',
      '24_months': 'Bi-Yearly'
    };
    return cycleMap[cycle] || cycle;
  };

  const formatDateUTC = (dateStr?: string | Date) =>
    dateStr ? new Date(dateStr).toLocaleDateString('de-DE', { timeZone: 'UTC' }) : '-';

  const getInvoiceStatusBadge = (status: string) => {
    const statusClasses: { [key: string]: string } = {
      draft: 'badge-secondary',
      finalized: 'badge-primary',
      sent: 'badge-info',
      paid: 'badge-success',
      cancelled: 'badge-warning',
      voided: 'badge-danger'
    };
    return statusClasses[status.toLowerCase()] || 'badge-secondary';
  };

  const getPaymentStatusBadge = (status: string) => {
    const statusClasses: { [key: string]: string } = {
      not_due: 'badge-secondary',
      pending: 'badge-warning',
      partial: 'badge-info',
      paid: 'badge-success',
      overdue: 'badge-danger',
      refunded: 'badge-info',
      disputed: 'badge-warning',
      written_off: 'badge-dark'
    };
    return statusClasses[status.toLowerCase()] || 'badge-secondary';
  };

  
  const getTotalContractValue = () => {
    return contractLines.reduce((total, line) => {
      const priceList = priceLists.find(p => p.pricelist_id === line.pricelist_id);
      if (!priceList) return total;
      
      // Calculate based on billing period
      let annualAmount = priceList.amount;
      switch (priceList.billing_period) {
        case '1_month':
          annualAmount = priceList.amount * 12;
          break;
        case '3_months':
          annualAmount = priceList.amount * 4;
          break;
        case '6_months':
          annualAmount = priceList.amount * 2;
          break;
        case '12_months':
          annualAmount = priceList.amount;
          break;
        case '24_months':
          annualAmount = priceList.amount * 0.5;
          break;
      }
      
      return total + annualAmount;
    }, 0);
  };

  if (loading && !contract) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="page-header">
        <h1>Contract not found</h1>
        <Link to="/contracts" className="btn btn-secondary">
          Back to Contracts
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>{contract.contract_name}</h1>
          <p style={{ color: '#7f8c8d', margin: '5px 0 0 0' }}>
            Contract #{contract.contract_number}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-primary" onClick={() => setShowContractModal(true)}>
            + Create Contract
          </button>
          <button className="btn btn-primary" onClick={handleCreateLine}>
            + Add Contract Line
          </button>
          {customer && customer.customer_type === 'supervisor' && customer.supervisor_id && (
            <button 
              className="btn btn-success" 
              onClick={handleAddAllControllers}
              disabled={addingControllers}
            >
              {addingControllers ? 'Adding Controllers...' : '+ Add All Controllers'}
            </button>
          )}
          <button className="btn btn-info" onClick={handleCreateInvoice}>
            Create Invoice
          </button>
          <button className="btn btn-warning" onClick={() => setShowBillingDateManager(true)} title="Manage and fix billing periods">
            Manage Billing Dates
          </button>
          <button
            className="btn btn-purple"
            onClick={async () => {
              try {
                setLoadingDebug(true);
                const response = await apiService.contracts.getBillingPeriodsDebug(contract.contract_id);
                setDebugInfo(response.debug_info);
                setShowDebugModal(true);
              } catch (error) {
                console.error('Failed to get billing period debug info:', error);
                console.error('Failed to load billing period debug information');
                // Show user-friendly error message
                const err = error as any;
                const errorMessage = err.response?.data?.detail || 'Failed to load billing period debug information. Please check the console for details.';
                alert(errorMessage);
              } finally {
                setLoadingDebug(false);
              }
            }}
            disabled={loadingDebug}
            title="Debug billing period calculation"
          >
            {loadingDebug ? 'Loading...' : '🔍 Debug Billing Periods'}
          </button>
          <button
            className="btn btn-warning ml-2"
            onClick={async () => {
              try {
                setIsRecalculating(true);
                await apiService.contracts.syncBillingPeriods(contract.contract_id);
                await fetchContractDetails(); // Refresh the data
                setBulkInvoiceFeedback({ type: 'success', text: 'Billing periods recalculated successfully!' });
              } catch (error) {
                console.error('Failed to recalculate billing periods:', error);
                setBulkInvoiceFeedback({ type: 'error', text: 'Failed to recalculate billing periods. See console for details.' });
              } finally {
                setIsRecalculating(false);
              }
            }}
            disabled={isRecalculating}
            title="Recalculate billing periods for this contract"
          >
            {isRecalculating ? 'Recalculating…' : '🔄 Recalculate Billing Periods'}
          </button>
          {contract.customer_id && (
            <button
              className="btn btn-outline-primary"
              onClick={() => navigate(`/customers/${contract.customer_id}`)}
              title="Open Customer Details"
            >
              Open Customer
            </button>
          )}
          <button 
            className="btn btn-success" 
            onClick={() => setActiveTab('recipients')}
          >
            View Recipients
          </button>
          <VatCheckButton
            type="single"
            targetId={contract.contract_id}
            buttonText="Check VAT"
            variant="info"
          />
          <Link to="/contracts" className="btn btn-secondary">
            Back to Contracts
          </Link>
        </div>
        
        {bulkInvoiceFeedback && (
          <div className={`alert alert-${bulkInvoiceFeedback.type === 'error' ? 'danger' : bulkInvoiceFeedback.type} mt-3`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
            <span>{bulkInvoiceFeedback.text}</span>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setBulkInvoiceFeedback(null)}
              aria-label="Dismiss feedback"
            >
              ×
            </button>
          </div>
        )}
      </div>

      {/* Contract Information */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Contract Information</h3>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button 
              className="btn btn-secondary btn-sm" 
              onClick={() => {
                setEditingContract(true);
                setShowContractModal(true);
              }}
            >
              Edit Contract
            </button>
            <span className={`badge ${contract.isactive ? 'badge-success' : 'badge-danger'}`}>
              {contract.isactive ? 'Active' : 'Inactive'}
            </span>
            <div style={{ marginLeft: '20px' }}>
              <strong>Invoicing Company:</strong>
              <span style={{ marginLeft: '10px', color: contract.company ? '#2c3e50' : '#e74c3c' }}>
                {contract.company ? (
                  <>
                    {contract.company.name}
                    {contract.company.legal_form && ` (${contract.company.legal_form})`}
                  </>
                ) : (
                  'Not assigned'
                )}
              </span>
            </div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          <div>
            <strong>Customer:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d', display: 'flex', alignItems: 'center' }}>
              <span>
                {customer?.customer_name || `ID ${contract.customer_id}`}
              </span>
              {contract.customer_id && (
                <button
                  className="btn btn-link btn-sm"
                  style={{ marginLeft: '8px', padding: 0 }}
                  onClick={() => navigate(`/customers/${contract.customer_id}`)}
                  title="Open Customer"
                >
                  ↗
                </button>
              )}
            </p>
            <ContractAssignment 
              contract={contract} 
              onUpdate={() => {
                fetchContractDetails();
                fetchContractLines();
              }}
            />
          </div>
          <div>
            <strong>Pricing Header:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {contract.pricing_header?.name || `Header ${contract.fixed_pricing_header_id}`}
              {contract.pricing_header?.category && (
                <span style={{ marginLeft: '10px', fontSize: '0.9em' }}>
                  ({contract.pricing_header.category})
                </span>
              )}
            </p>
          </div>
          <div>
            <strong>Currency:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {contract.currency}
            </p>
          </div>
          <div>
            <strong>Payment Method:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {contract.payment_method ? contract.payment_method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Not specified'}
            </p>
          </div>
          <div>
            <strong>Due Day of Month:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {contract.due_day_of_month || 'Not specified'}
            </p>
          </div>
          <div>
            <strong>Language:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {contract.language ? contract.language.toUpperCase() : 'Not specified'}
            </p>
          </div>
          <div>
            <strong>Billing Cycle:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {formatBillingCycle(contract.billing_cycle)}
            </p>
          </div>
          <div>
            <strong>Start Date:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {formatDateUTC(contract.start_date)}
            </p>
          </div>
          <div>
            <strong>End Date:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {contract.end_date ? formatDateUTC(contract.end_date) : 'No end date'}
            </p>
          </div>
          <div>
            <strong>Contract Length:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {contract.contract_length.replace('_', ' ')}
            </p>
          </div>
          <div>
            <strong>Notice Period:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {contract.notice_period.replace('_', ' ')}
            </p>
          </div>
          <div>
            <strong>Created By:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {contract.contract_createdby}
            </p>
          </div>
          <div>
            <strong>Created:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {formatDateUTC(contract.contract_createdat)}
            </p>
          </div>
          {contract.contract_updatedby && (
            <div>
              <strong>Updated By:</strong>
              <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
                {contract.contract_updatedby}
              </p>
            </div>
          )}
          {contract.contract_updatedat && (
            <div>
              <strong>Updated:</strong>
              <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
                {formatDateUTC(contract.contract_updatedat)}
              </p>
            </div>
          )}
          <div>
            <strong>Total Annual Value:</strong>
            <p style={{ margin: '5px 0', color: '#3498db', fontWeight: 'bold' }}>
              {formatCurrency(getTotalContractValue(), contract.currency)}
              <span style={{ fontSize: '0.8em', fontWeight: 'normal', marginLeft: '5px' }}>
                /year
              </span>
            </p>
          </div>
          <div>
            <strong>Current Pricing:</strong>
            <p style={{ margin: '5px 0', color: '#7f8c8d' }}>
              {contract.is_current_pricing_header ? 'Yes' : 'No'}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs for Contract Lines, PDFs, and Invoices */}
      <div className="card">
        <div className="tabs">
          <div className="tab-list">
            <button
              className={`tab ${activeTab === 'lines' ? 'active' : ''}`}
              onClick={() => setActiveTab('lines')}
            >
              Contract Lines ({contractLines.length})
            </button>
            <button
              className={`tab ${activeTab === 'documents' ? 'active' : ''}`}
              onClick={() => setActiveTab('documents')}
            >
              Documents
            </button>
            <button
              className={`tab ${activeTab === 'invoices' ? 'active' : ''}`}
              onClick={() => setActiveTab('invoices')}
            >
              Invoices ({contractInvoices.length})
            </button>
            <button
              className={`tab ${activeTab === 'recipients' ? 'active' : ''}`}
              onClick={() => setActiveTab('recipients')}
            >
              Recipients
            </button>
          </div>
        </div>

        {/* Contract Lines Tab */}
        {activeTab === 'lines' && (
          <div>
          {contractLines.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <h3 style={{ color: '#7f8c8d', marginBottom: '10px' }}>No Contract Lines</h3>
              <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
                This contract doesn't have any lines configured yet.
              </p>
              <button className="btn btn-primary" onClick={handleCreateLine}>
                Add First Contract Line
              </button>
            </div>
          ) : (
            <>
              {selectedContractLines.length > 0 && (
                <div style={{ padding: '10px', backgroundColor: '#f8f9fa', marginBottom: '10px', display: 'flex', gap: '10px' }}>
                  <button 
                    className="btn btn-info btn-sm"
                    onClick={handleChangePlanBulk}
                  >
                    Change Plans ({selectedContractLines.length} selected)
                  </button>
                  <button 
                    className="btn btn-warning btn-sm"
                    onClick={handleBulkEdit}
                  >
                    Bulk Edit ({selectedContractLines.length} selected)
                  </button>
                </div>
              )}
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={selectedContractLines.length === contractLines.length && contractLines.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedContractLines(contractLines.map(l => l.contractline_id));
                            } else {
                              setSelectedContractLines([]);
                            }
                          }}
                        />
                      </th>
                      <th>Number</th>
                      <th>Name</th>
                      <th>Price List</th>
                      <th>Plan</th>
                      <th>Amount</th>
                      <th>Tax Rate</th>
                      <th>Discount</th>
                      <th>Billing Cycle</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Last Billing</th>
                      <th>Upcoming Billing</th>
                      <th>Trial Period</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                <tbody>
                    {contractLines.map((line) => {
                      const priceListDetails = getPriceListDetails(line.pricelist_id);
                      return (
                        <tr key={line.contractline_id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedContractLines.includes(line.contractline_id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedContractLines(prev => [...prev, line.contractline_id]);
                                } else {
                                  setSelectedContractLines(prev => prev.filter(id => id !== line.contractline_id));
                                }
                              }}
                            />
                          </td>
                          <td>{line.contractline_number}</td>
                          <td>{line.contractline_name}</td>
                          <td>{priceListDetails?.name || `Price List ${line.pricelist_id}`}</td>
                          <td>{priceListDetails?.planName || '-'}</td>
                          <td>
                            <DebouncedCustomAmountInput
                              initialValue={line.custom_amount !== null && line.custom_amount !== undefined 
                                ? line.custom_amount 
                                : priceListDetails?.amount || 0}
                              onUpdate={async (customAmount) => {
                                await apiService.updateContractLine(line.contractline_id, {
                                  custom_amount: customAmount
                                });
                                fetchContractLines();
                              }}
                              placeholder={priceListDetails ? formatCurrency(priceListDetails.amount, priceListDetails.currency) : "Enter amount"}
                            />
                          </td>
                          <td>
                            {line.tax_rate !== undefined && line.tax_rate !== null ? (
                              <span>{line.tax_rate}%</span>
                            ) : (
                              <span style={{ color: '#999' }}>-</span>
                            )}
                          </td>
                          <td>
                            {line.discount_type && line.discount_value !== undefined && line.discount_value !== null ? (
                              <span className="badge badge-success">
                                {line.discount_type === 'percentage' 
                                  ? `${(line.discount_value / 100).toFixed(0)}%`
                                  : formatCurrency(line.discount_value / 100, priceListDetails?.currency || 'EUR')
                                }
                              </span>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td>
                            <span className="badge badge-info">
                              {formatBillingCycle(line.billing_cycle)}
                            </span>
                          </td>
                          <td>{formatDateUTC(line.start)}</td>
                          <td>{line.end ? formatDateUTC(line.end) : '-'}</td>
                          <td>
                            {line.last_billing_start ? `${formatDateUTC(line.last_billing_start)} - ${line.last_billing_end ? formatDateUTC(line.last_billing_end) : '-'}` : '-'}
                          </td>
                          <td>
                            {line.upcoming_billing_start ? `${formatDateUTC(line.upcoming_billing_start)} - ${line.upcoming_billing_end ? formatDateUTC(line.upcoming_billing_end) : '-'}` : '-'}
                          </td>
                          <td>{line.trial_period} days</td>
                          <td>
                            <span className={`badge ${line.is_active ? 'badge-success' : 'badge-danger'}`}>
                              {line.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <button
                              className="btn btn-info btn-sm"
                              onClick={() => handleChangePlanSingle(line)}
                              style={{ marginRight: '5px' }}
                              title="Change Plan"
                            >
                              Plan
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleEditLine(line)}
                              style={{ marginRight: '5px' }}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDeleteLine(line.contractline_id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
          </div>
        )}

        {/* Contract Documents Tab */}
        {activeTab === 'documents' && (
          <ContractDocuments 
            contract={contract} 
            onUpdate={() => {
              fetchContractDetails();
            }}
          />
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div>
            {contractInvoices.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <h3 style={{ color: '#7f8c8d', marginBottom: '10px' }}>No Invoices</h3>
                <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
                  This contract doesn't have any invoices yet.
                </p>
                <button className="btn btn-primary" onClick={handleCreateInvoice}>
                  Create First Invoice
                </button>
              </div>
            ) : (
              <div className="table-container">
                <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      className={`btn btn-sm ${showFinalizedOnly ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setShowFinalizedOnly(!showFinalizedOnly)}
                      title={showFinalizedOnly ? 'Show all invoices' : 'Show finalized invoices only'}
                    >
                      {showFinalizedOnly ? '🔓 Show All Invoices' : '✓ Finalized Only'}
                    </button>
                    
                    {/* Language Selection and Download Button */}
                    <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                      <select
                        className="form-select form-select-sm"
                        style={{ width: '100px' }}
                        value={selectedReportLanguage}
                        onChange={(e) => setSelectedReportLanguage(e.target.value as 'en' | 'de' | 'pt')}
                        title="Select report language"
                      >
                        <option value="en">English</option>
                        <option value="de">Deutsch</option>
                        <option value="pt">Português</option>
                      </select>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={async () => {
                          if (!contract?.contract_id) return;
                          
                          try {
                            setDownloadingReport(true);
                            const blob = await apiService.downloadContractInvoiceReport(
                              contract.contract_id,
                              selectedReportLanguage,
                              showFinalizedOnly
                            );
                            
                            // Create download link
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            const customerName = customer?.customer_name || 'Unknown';
                            const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '');
                            a.download = `${dateStr}_${customerName.replace(/[<>:"/\\|?*\s]/g, '_')}_invoice_overview.pdf`;
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                          } catch (error: any) {
                            console.error('Error downloading report:', error);
                            alert(error.message || 'Failed to download report. Please try again.');
                          } finally {
                            setDownloadingReport(false);
                          }
                        }}
                        disabled={downloadingReport}
                        title="Download invoice overview report as PDF"
                      >
                        {downloadingReport ? '📄 Generating...' : '📄 Download Report'}
                      </button>
                    </div>
                  </div>
                  <span style={{ color: '#7f8c8d', fontSize: '0.9em' }}>
                    Showing {showFinalizedOnly ? contractInvoices.filter(inv => inv.invoice_status === 'finalized').length : contractInvoices.length} of {contractInvoices.length} invoices
                  </span>
                </div>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Invoice Name</th>
                      <th>Invoice Date</th>
                      <th>Due Date</th>
                      <th>Billing Period</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Payment Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contractInvoices.filter(invoice => !showFinalizedOnly || invoice.invoice_status === 'finalized').map((invoice) => (
                      <tr key={invoice.invoice_id}>
                        <td>{invoice.invoice_name || invoice.invoice_number}</td>
                        <td>{formatDateUTC(invoice.invoice_date)}</td>
                        <td>{formatDateUTC(invoice.due_date)}</td>
                        <td>
                          {formatDateUTC(invoice.billing_period_start)} - {formatDateUTC(invoice.billing_period_end)}
                        </td>
                        <td>{formatCurrency(invoice.total_amount, invoice.currency)}</td>
                        <td>
                          <span className={`badge ${getInvoiceStatusBadge(invoice.invoice_status)}`}>
                            {invoice.invoice_status}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${getPaymentStatusBadge(invoice.payment_status)}`}>
                            {invoice.payment_status}
                          </span>
                        </td>
                        <td>
                          <Link
                            to={`/invoices/${invoice.invoice_id}`}
                            className="btn btn-secondary btn-sm"
                            style={{ marginRight: '5px' }}
                          >
                            View
                          </Link>
                                                  </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
        {activeTab === 'recipients' && (
          <ContractRecipients 
            contract={contract} 
            onUpdate={() => {
              fetchContractDetails();
            }}
          />
        )}
      {showLineModal && (
        <Modal
          title={editingLine ? 'Edit Contract Line' : 'Add Contract Line'}
          onClose={() => setShowLineModal(false)}
        >
          <ContractLineForm
            contractId={id!}
            initialData={editingLine}
            priceLists={priceLists}
            onSubmit={handleSubmitLine}
            onCancel={() => setShowLineModal(false)}
          />
        </Modal>
      )}

      {showContractModal && (
        <Modal 
          title={editingContract ? "Edit Contract" : "Create Contract"} 
          onClose={() => {
            setShowContractModal(false);
            setEditingContract(false);
          }}
        >
          <ContractForm
            initialData={editingContract ? contract : null}
            pricingHeaders={pricingHeaders}
            customers={customers}
            onSubmit={handleSubmitContract}
            onCancel={() => {
              setShowContractModal(false);
              setEditingContract(false);
            }}
          />
        </Modal>
      )}

      {showChangePlanModal && (
        <ChangePlanModal
          contractLines={selectedLinesForPlanChange}
          priceLists={priceLists}
          onSubmit={handlePlanChangeSubmit}
          onCancel={() => {
            setShowChangePlanModal(false);
            setSelectedLinesForPlanChange([]);
          }}
          singleLine={selectedLinesForPlanChange.length === 1 ? selectedLinesForPlanChange[0] : undefined}
        />
      )}

      {showBulkEditModal && (
        <BulkEditContractLinesModal
          contractLines={contractLines.filter(line => selectedContractLines.includes(line.contractline_id))}
          priceLists={priceLists}
          onSubmit={handleBulkEditSubmit}
          onCancel={() => {
            setShowBulkEditModal(false);
          }}
        />
      )}

      {showBillingDateManager && (
        <BillingDateManagerModal
          isOpen={showBillingDateManager}
          onClose={() => setShowBillingDateManager(false)}
          onSuccess={() => {
            // After fixes, refresh invoices and lines to reflect updated periods
            fetchContractInvoices();
            fetchContractLines();
          }}
        />
      )}
    {showDebugModal && debugInfo && (
      <DebugBillingPeriodsModal
        debugInfo={debugInfo}
        onClose={() => setShowDebugModal(false)}
      />
    )}
    </div>
  );
}

export default ContractDetails;